<script setup>
import {computed, useSlots, ref, onMounted, onUnmounted, nextTick, watch} from "vue";
import IconCross from "@/assets/icons/IconCross.vue";
import InputSearchSecondary from "@/components/Form/InputSearchSecondary.vue";
import ButtonAccentPrimary from "@/components/Buttons/Default/ButtonAccentPrimary.vue";
import { useClickOutsideDropdown } from "@/components/Dropdown/useClickOutsideDropdown";

const props = defineProps({
  title: {
    type: String,
    default: ""
  },
  useCrossIcon: {
    type: Boolean,
    default: true
  },
  useConfirm: {
    type: Boolean,
    default: false
  },
  confirmButtonText: {
    type: String,
    default: "Кнопка"
  },
  /**
   * Расположение dropdown относительно триггера
   *          bottom-left, bottom-center, bottom-right,
   *          top-left, top-center, top-right,
   *          left, right
   */
  position: {
    type: String,
    default: "bottom-left",
    validator: (value) => [
      "bottom-left", "bottom-center", "bottom-right",
      "top-left", "top-center", "top-right",
      "left", "right"
    ].includes(value)
  },
  isVisible: {
    type: Boolean,
    default: false
  },
  teleport: {
    type: String,
    default: "#appMain"
  },
  useSearchbar: {
    type: Boolean,
    default: false
  },
  searchBarQuery: {
    type: String,
    default: ""
  },
  width: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  },
  height: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  },
  maxWidth: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  },
  maxHeight: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  },
  triggerHeight: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  },
  offset: {
    type: [String, Number],
    default: 10,
  },
  matchTriggerWidth: {
    type: Boolean,
    default: false
  },
  triggerClass: {
    type: String,
    default: ""
  }
});

const emit = defineEmits(["close", "confirm", "update:isVisible", "update:search-bar-query"]);

const triggerWidth = ref(null);

const computedTriggerHeight = computed(() => {
  if (!props.triggerHeight) return null;
  if (typeof props.triggerHeight === "number") {
    return `${props.triggerHeight}px`;
  }
  return props.triggerHeight;
});

const computedMaxHeight = computed(() => {
  if (!props.maxHeight) return null;
  if (typeof props.maxHeight === "number") {
    return `${props.maxHeight}px`;
  }
  return props.maxHeight;
});

const computedMaxWidth = computed(() => {
  if (!props.maxWidth) return "315px";
  if (typeof props.maxWidth === "number") {
    return `${props.maxWidth}px`;
  }
  return props.maxWidth;
});

const computedHeight = computed(() => {
  if (!props.height) return null;
  if (typeof props.height === "number") {
    return `${props.height}px`;
  }
  return props.height;
});

const computedWidth = computed(() => {
  // Если включен matchTriggerWidth, используем ширину триггера
  if (props.matchTriggerWidth && triggerWidth.value) {
    return `${triggerWidth.value}px`;
  }
  if (!props.width) return "315px";
  if (typeof props.width === "number") {
    return `${props.width}px`;
  }
  return props.width;
});

// Отслеживаем изменения isVisible для обновления позиции
watch(() => props.isVisible, (newVal) => {
  if (newVal) {
    nextTick(() => {
      // Добавляем небольшую задержку для корректного рендеринга dropdown
      updateDropdownPosition();
    });
  }
});

const slots = useSlots();

const dropdownRef = ref(null);
const triggerRef = ref(null);
const dropdownPosition = ref({ top: 0, left: 0 });

// Клик вне dropdown
useClickOutsideDropdown(triggerRef, (event) => {
  if (props.isVisible) {
    if (props.useConfirm) {
      handleClose(event);
    } else {
      emit("close", event);
    }
  }
});

// Рассчет позиции для dropdown (по аналогии с GlobusCalendar)
function updateDropdownPosition() {
  const t = triggerRef.value;
  if (!t) return;

  const rect = t.getBoundingClientRect();
  const scrollY = window.scrollY;
  const scrollX = window.scrollX;

  // Сохраняем ширину триггера для matchTriggerWidth
  if (props.matchTriggerWidth) {
    triggerWidth.value = rect.width;
  }

  const offset = props.offset;
  const dropdownEl = dropdownRef.value;
  const dw = dropdownEl?.offsetWidth || 200;
  const dh = dropdownEl?.offsetHeight || 160;

  let top = 0;
  let left = 0;

  switch (props.position) {
    case "bottom-left":
      top = rect.bottom + scrollY + offset;
      left = rect.left + scrollX;
      break;
    case "bottom-center":
      top = rect.bottom + scrollY + offset;
      left = rect.left + scrollX + rect.width / 2 - dw / 2;
      break;
    case "bottom-right":
      top = rect.bottom + scrollY + offset;
      left = rect.right + scrollX - dw;
      break;
    case "top-left":
      top = rect.top + scrollY - dh - offset;
      left = rect.left + scrollX;
      break;
    case "top-center":
      top = rect.top + scrollY - dh - offset;
      left = rect.left + scrollX + rect.width / 2 - dw / 2;
      break;
    case "top-right":
      top = rect.top + scrollY - dh - offset;
      left = rect.right + scrollX - dw;
      break;
    case "left":
      top = rect.top + scrollY + rect.height / 2 - dh / 2;
      left = rect.left + scrollX - dw - offset;
      break;
    case "right":
      top = rect.top + scrollY + rect.height / 2 - dh / 2;
      left = rect.right + scrollX + offset;
      break;
    default:
      // По умолчанию нижний левый
      top = rect.bottom + scrollY + offset;
      left = rect.left + scrollX;
  }


  // Проверка границ viewport (учитываем скролл)
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const currentViewportTop = scrollY;
  const currentViewportBottom = scrollY + viewportHeight;

  // Горизонтальные границы
  if (left + dw > viewportWidth) {
    left = viewportWidth - dw - offset;
  }
  if (left < offset) {
    left = offset;
  }

  // Вертикальные границы - проверяем относительно видимой области
  if (top + dh > currentViewportBottom) {
    // Dropdown выходит за нижнюю границу - показываем сверху от trigger
    top = rect.top + scrollY - dh - offset;
  }
  if (top < currentViewportTop) {
    // Dropdown выходит за верхнюю границу - показываем снизу
    top = rect.bottom + scrollY + offset;
  }

  dropdownPosition.value = { top, left };
}

const handleResize = () => {
  updateDropdownPosition();
};

onMounted(() => {
  nextTick(() => {
    updateDropdownPosition();
  });
  window.addEventListener("resize", handleResize);
  window.addEventListener("scroll", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("scroll", handleResize);
});


// Состояние для поиска
const searchBarQuery = computed({
  get() {
    return props.searchBarQuery;
  },
  set(value) {
    emit("update:search-bar-query", value);
  }
});

const handleConfirm = () => {
  emit("confirm");
};

const handleClose = (event) => {
  emit("close", event);
};

</script>

<template>
  <div
    ref="triggerRef"
    class="dropdown-trigger"
    :class="props.triggerClass"
    :style="{'height': computedTriggerHeight}"
  >
    <slot name="trigger" />

    <Teleport :to="props.teleport">
      <div
        v-if="isVisible"
        ref="dropdownRef"
        class="dropdown"
        :style="{ position: 'absolute', top: dropdownPosition.top + 'px', left: dropdownPosition.left + 'px', zIndex: 9999, width: computedWidth, height: computedHeight, 'max-height': computedMaxHeight, 'max-width': computedMaxWidth }"
      >
        <div
          v-if="props.title || slots['title-icon'] || props.useCrossIcon"
          class="dropdown-header"
        >
          <div
            v-if="props.title || slots['title-icon']"
            class="dropdown-header__left"
          >
            <div
              v-if="slots['title-icon']"
              class="dropdown-header__icon-user"
            >
              <slot name="title-icon" />
            </div>
            <span
              v-if="props.title"
              class="dropdown-header__title"
            >
              {{ props.title }}
            </span>
          </div>
          <IconCross
            v-if="props.useCrossIcon"
            class="dropdown-header__icon-cross"
            @click="props.useConfirm ? handleClose() : emit('close')"
          />
        </div>

        <div
          v-if="props.useSearchbar"
          class="dropdown__search"
        >
          <InputSearchSecondary
            v-model="searchBarQuery"
            placeholder="Поиск"
          />
        </div>

        <ul class="dropdown__list scroll">
          <slot name="dropdown-item" />
        </ul>

        <div
          v-if="slots['footer']"
          class="dropdown__footer"
        >
          <slot name="footer" />
        </div>
        <div
          v-else-if="!slots['footer'] && props.useConfirm"
          class="dropdown__footer"
        >
          <ButtonAccentPrimary
            v-if="props.useConfirm"
            size="large"
            @click="handleConfirm"
          >
            {{ props.confirmButtonText }}
          </ButtonAccentPrimary>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.dropdown-trigger {
  position: relative;
  display: inline-block;
}

.dropdown {
  position: absolute;
  z-index: 9999;
  background: var(--white);
  border-radius: 8px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
  width: 200px;
  //min-height: 120px;
  //max-height: 260px;

  &__list {
    list-style: none;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0 2px 0 0;

  }

  &__footer {
    display: flex;
    button { flex-grow: 1; }
  }

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__left {
      display: flex;
      align-items: center;
      gap: 6px;
      flex: 1;
      min-width: 0;
      overflow: hidden;
    }

    &__icon {
      &-user {
        display: flex;
        justify-content: center;
        align-items: center;
        color: var(--text-text-pur);
      }

      &-cross {
        cursor: pointer;
        user-select: none;
        color: var(--text-text-pur);
        width: 16px;
        height: 16px;

        &:hover {
          color: var(--text-text-on);
        }
      }
    }

    &__title {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      font-weight: 500;
      font-size: 14px;
      color: var(--text-text-pur);
      flex: 1;
      min-width: 0;

    }
  }


}

// Стили для скрола
.scroll {
  overflow-y: auto;

  @-moz-document url-prefix() {
    scrollbar-width: thin;
    scrollbar-color: #1A64D433 transparent;
  }

  &::-webkit-scrollbar {
    width: 7px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #1A64D433;
    border-radius: 30px;
  }
}
</style>